import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import ScribeHeader from "./ScribeHeader";
import AudioTab from "./tabs/AudioTab";
import RecordTab from "./tabs/RecordTab";
import DocumentTab from "./tabs/DocumentTab";
import ExtractChatTab from "./tabs/ExtractChatTab";
import PasteTab from "./tabs/PasteTab";
import QaTab from "./tabs/QaTab";
import FormFillerTab from "./tabs/FormFillerTab";
import { type TabId } from "../../constants/scribeUi";
import { RECORD_TAB_ID } from "../../constants/scribeTabs";
import { useScribeNav } from "../../contexts/ScribeNavContext";

interface TabPanelProps {
  children: React.ReactNode;
  value: TabId;
  id: TabId;
  /** Keep children mounted (just hidden) while another tab is active. */
  keepMounted?: boolean;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, id, keepMounted = false }) => (
  <Box role="tabpanel" hidden={value !== id} sx={{ pt: { xs: 2, sm: 2.5 } }}>
    {(keepMounted || value === id) && children}
  </Box>
);

const DOC_CHAT_TAB_ID: TabId = "docChat";

// The tool navigation (left rail, or bottom bar on phones) lives in Layout and shares the active
// tool through ScribeNavContext; this component renders only the active tool's panel.
const ScribeMain: React.FC<{ isApproved: boolean }> = ({ isApproved }) => {
  const { visibleTabs, activeTab, selectTab, setIsRecording } = useScribeNav();
  const isTabVisible = (id: TabId) => visibleTabs.some((t) => t.id === id);
  const [injectedDocChatText, setInjectedDocChatText] = useState("");
  // Record mounts when it first becomes the active tab (on load when it is first in
  // SCRIBE_VISIBLE_TABS, otherwise on its first visit) and then stays mounted, so a live
  // recording survives a tab switch.
  const [recordVisited, setRecordVisited] = useState(false);

  useEffect(() => {
    if (activeTab === RECORD_TAB_ID) setRecordVisited(true);
  }, [activeTab]);

  // When the page unmounts (another page), the recorder goes with it: clear the shared flag.
  useEffect(() => () => setIsRecording(false), [setIsRecording]);

  // No-ops while Doc Chat is hidden, so a hidden tool never steals the selection.
  const docChatVisible = isTabVisible(DOC_CHAT_TAB_ID);
  const handleSendToDocChat = useCallback(
    (combinedText: string) => {
      if (!docChatVisible) return;
      setInjectedDocChatText(combinedText);
      selectTab(DOC_CHAT_TAB_ID);
    },
    [docChatVisible, selectTab],
  );

  const handleDocChatTextConsumed = useCallback(() => {
    setInjectedDocChatText("");
  }, []);

  // Panels are matched by tab id, never by position, so the list can be reordered or trimmed.
  // Record is rendered separately below (it stays mounted once visited).
  const renderPanel = (id: TabId): React.ReactNode => {
    switch (id) {
      case "audio":
        return <AudioTab isApproved={isApproved} />;
      case "document":
        return <DocumentTab isApproved={isApproved} />;
      case "docChat":
        return (
          <ExtractChatTab
            isApproved={isApproved}
            injectedText={injectedDocChatText}
            onInjectedTextConsumed={handleDocChatTextConsumed}
          />
        );
      case "paste":
        return <PasteTab isApproved={isApproved} />;
      case "qa":
        return <QaTab isApproved={isApproved} onSendToDocChat={handleSendToDocChat} />;
      case "formFiller":
        return <FormFillerTab isApproved={isApproved} />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 980,
        mx: "auto",
        px: { xs: 1.5, sm: 2, md: 3 },
        pt: { xs: 2.5, sm: 3.5 },
        pb: 8,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <ScribeHeader />

      {visibleTabs
        .filter((t) => t.id !== RECORD_TAB_ID)
        .map((t) => (
          <TabPanel key={t.id} value={activeTab} id={t.id}>
            {renderPanel(t.id)}
          </TabPanel>
        ))}
      {/* Record stays mounted once visited so an active MediaRecorder, its chunks and
          the mic stream survive a tab switch. It is rendered after the other panels
          (whatever its position in the tab bar) so DOM-order selectors on the other tabs, such as the
          first textarea on the Paste tab, never land on this hidden panel. */}
      {isTabVisible(RECORD_TAB_ID) && (
        <TabPanel value={activeTab} id={RECORD_TAB_ID} keepMounted={recordVisited || activeTab === RECORD_TAB_ID}>
          <RecordTab
            isApproved={isApproved}
            onRecordingChange={setIsRecording}
            active={activeTab === RECORD_TAB_ID}
          />
        </TabPanel>
      )}
    </Box>
  );
};

export default ScribeMain;
